import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
    Calendar,
    Clock,
    User,
    FileText,
    CheckCircle,
    XCircle,
    AlertCircle,
    Briefcase,
} from 'lucide-react'

import {
    cancelDayOff,
    getDayOffByID,
    approveLeadDayOff,
    approveBodDayOff,
} from '../../services'
import { Button, Chip, Typogaphy } from 'vnetwork-ui'
import { GridInformation } from 'vnetwork-ui'
import { useRefetchDayOffQueries } from '../../constants'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { DayOffRangeTime, DayOffResponse } from '../../types/response'
import { useConfirmation } from '@/contexts/confirmation'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { formatFromUTC } from '@/utils/function'
import { useModal } from '@/contexts/modal'
import { RejectModalProps } from '@/modals/DayOffModals/RejectModal'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import Paper from '@/components/Paper'
import ButtonGroupActions from '@/components/ButtonGroupActions'
import CloseIcons from '@/icons/CloseIcons'

// Status color mapping
const getStatusColor = (status: string) => {
    switch (status) {
        case 'approved':
            return 'success'
        case 'pending':
        case 'leader-approved':
            return 'warning'
        case 'rejected':
        case 'cancelled':
            return 'error'
        default:
            return 'default'
    }
}

// Day off type color
const getDayOffTypeColor = (type: string) => {
    switch (type) {
        case 'annual_leave':
            return 'primary'
        case 'sick_leave':
            return 'error'
        case 'unpaid_leave':
            return 'warning'
        case 'maternity_leave':
            return 'secondary'
        default:
            return 'default'
    }
}

// Format day off option
const formatDayOffOption = (option: number, t: (key: string) => string) => {
    switch (option) {
        case 0:
            return t('morning')
        case 1:
            return t('afternoon')
        case 2:
            return t('all_day')
        default:
            return '-'
    }
}

// Day Off Date Card Component
const DayOffDateCard: React.FC<{
    item: DayOffRangeTime
    t: (key: string) => string
}> = ({ item, t }) => {
    const optionColor =
        item.day_off_option === 2 ? 'bg-blue-100' : 'bg-amber-100'
    const optionTextColor =
        item.day_off_option === 2 ? 'text-blue-700' : 'text-amber-700'

    return (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="p-2 bg-white rounded-md shadow-sm">
                <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
                <Typogaphy className="font-medium capitalize">
                    {dayjs(item.start_day_off).format('dddd, DD/MM/YYYY')}
                </Typogaphy>
                <span
                    className={`text-xs px-2 py-0.5 rounded-full ${optionColor} ${optionTextColor}`}
                >
                    {formatDayOffOption(item.day_off_option, t)}
                </span>
            </div>
        </div>
    )
}

// Approval Status Card with Approve Form
const ApprovalStatusCard: React.FC<{
    title: string
    isApproved: boolean
    approverName?: string
    approvedAt?: string
    feedback?: string
    icon: React.ReactNode
    t: (key: string) => string
    canApprove?: boolean
    isApproving?: boolean
    onApprove?: (feedback: string) => void
    waitingForDependency?: string // e.g., "Chờ Leader duyệt"
    isDisabled?: boolean // When cancelled or rejected
}> = ({
    title,
    isApproved,
    approverName,
    approvedAt,
    feedback,
    icon,
    t,
    canApprove,
    isApproving,
    onApprove,
    waitingForDependency,
    isDisabled,
}) => {
    const [feedbackInput, setFeedbackInput] = useState('Approved')

    const handleApprove = () => {
        if (onApprove) {
            onApprove(feedbackInput)
        }
    }

    // Determine status display
    const getStatusLabel = () => {
        if (isApproved) return t('approved')
        return t('pending')
    }

    // Determine card background
    const getCardStyle = () => {
        if (isDisabled) return 'bg-gray-100 border-gray-300 opacity-60'
        if (isApproved) return 'bg-green-50 border-green-200'
        return 'bg-gray-50 border-gray-200'
    }

    return (
        <div className={`p-3 rounded-lg border ${getCardStyle()}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    {icon}
                    <Typogaphy className="font-semibold text-sm">
                        {title}
                    </Typogaphy>
                </div>
                <Chip
                    label={getStatusLabel()}
                    color={
                        isApproved
                            ? 'success'
                            : isDisabled
                              ? 'default'
                              : 'warning'
                    }
                    size="small"
                />
            </div>
            {isApproved && approverName && (
                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-medium">{approverName}</span>
                </div>
            )}
            {isApproved && approvedAt && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span>{formatFromUTC(approvedAt)}</span>
                </div>
            )}
            {feedback && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                    <Typogaphy size="small" className="text-gray-600 italic">
                        "{feedback}"
                    </Typogaphy>
                </div>
            )}
            {/* Waiting for dependency (e.g., BOD waiting for Leader) */}
            {!isApproved && !isDisabled && waitingForDependency && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{waitingForDependency}</span>
                </div>
            )}
            {/* Waiting for approval (no dependency) */}
            {!isApproved &&
                !isDisabled &&
                !canApprove &&
                !waitingForDependency && (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{t('waiting_for_approval')}</span>
                    </div>
                )}
            {/* Approve Form */}
            {canApprove && !isApproved && !isDisabled && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                    <textarea
                        value={feedbackInput}
                        onChange={(e) => setFeedbackInput(e.target.value)}
                        placeholder={t('feedback_placeholder')}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                        rows={2}
                    />
                    <Button
                        size="small"
                        onClick={handleApprove}
                        loading={isApproving}
                        disabled={isApproving}
                        className="w-full"
                    >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {t('approve')}
                    </Button>
                </div>
            )}
        </div>
    )
}

const Detail: React.FC = () => {
    const { t } = useTranslation('dayoffs')
    const { setConfirmation } = useConfirmation()
    const { employee } = useAuthStore()
    const { isAdmin } = usePermissionsStore()
    const { id: idParam } = useParams<{ id: string }>()
    const { setModal } = useModal()

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DAY_OFFS, idParam],
        queryFn: async () => await getDayOffByID(Number(idParam) || 0),
    })

    const employeeRefetch = useDataGridRefetch({
        reactQueryKey: 'verify-token',
    })

    const { refetchAll } = useRefetchDayOffQueries()

    const { mutateAsync: cancelDO, isPending: isCancelingDO } =
        useMutationWithHandler(
            { mutationFn: cancelDayOff },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: 'something went wrong',
            }
        )

    const { mutateAsync: leaderApprove, isPending: isLeaderApproving } =
        useMutationWithHandler(
            { mutationFn: approveLeadDayOff },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: 'something went wrong',
            }
        )

    const { mutateAsync: bodApprove, isPending: isBodApproving } =
        useMutationWithHandler(
            { mutationFn: approveBodDayOff },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: 'something went wrong',
            }
        )

    const dayOffData = data?.result
    const isOwner = employee?.employee_id === dayOffData?.employee_id
    const canCancel = dayOffData?.day_off_status === 'pending' && isOwner
    const canReject = dayOffData?.day_off_status === 'pending'

    // Approval permissions
    const isCancelledOrRejected =
        dayOffData?.day_off_status === 'cancelled' ||
        dayOffData?.day_off_status === 'rejected'
    const isLeaderApproved = dayOffData?.day_off_leader_confirmed
    const isBodApproved = dayOffData?.day_off_bod_confirmed
    // If BOD approved but Leader didn't → Leader was skipped
    const isLeaderSkipped = isBodApproved && !isLeaderApproved
    const canLeaderApprove =
        dayOffData?.day_off_status === 'pending' &&
        !isLeaderApproved &&
        !isBodApproved
    // Admin can approve BOD even if Leader hasn't approved yet
    const canBodApprove = isAdmin
        ? (dayOffData?.day_off_status === 'pending' ||
              dayOffData?.day_off_status === 'leader-approved') &&
          !isBodApproved
        : dayOffData?.day_off_status === 'leader-approved' && !isBodApproved

    // Grid data for information display
    const gridData = useMemo(() => {
        if (!dayOffData) return []
        return [
            {
                label: t('day_off_id'),
                value: `#${dayOffData.day_off_id}`,
            },
            {
                label: t('employee_name'),
                value: (
                    <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{dayOffData.employee_name}</span>
                    </div>
                ),
            },
            {
                label: t('department'),
                value: (
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span>{dayOffData.department_name || '-'}</span>
                    </div>
                ),
            },
            {
                label: t('position'),
                value: dayOffData.position_name || '-',
            },
            {
                label: t('day_off_created'),
                value: formatFromUTC(dayOffData.day_off_created_at),
            },
            {
                label: t('day_off_status'),
                value: (
                    <Chip
                        label={t(dayOffData.day_off_status || '')}
                        color={getStatusColor(dayOffData.day_off_status || '')}
                        size="small"
                    />
                ),
            },
            {
                label: t('day_off_type'),
                value: (
                    <Chip
                        label={t(dayOffData.day_off_type || '')}
                        color={getDayOffTypeColor(
                            dayOffData.day_off_type || ''
                        )}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                label: t('number_of_day_off'),
                value: (
                    <span className="font-semibold text-primary text-lg">
                        {dayOffData.number_of_day_off} {t('days')}
                    </span>
                ),
            },
            {
                label: '',
                value: '',
            },
            {
                label: t('day_off_reason'),
                value: (
                    <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span className="text-gray-700">
                            {dayOffData.day_off_reason || '-'}
                        </span>
                    </div>
                ),
                className: 'lg:col-span-4',
            },
        ]
    }, [dayOffData, t])

    const handleCancel = async () => {
        if (!dayOffData?.day_off_id) return
        try {
            await cancelDO(dayOffData.day_off_id)
            toast.success(t('cancel_success'))
            if (dayOffData.day_off_type === 'annual_leave') {
                employeeRefetch.refetchCurrent()
            }
            refetch()
        } catch (error) {
            console.error(error)
        }
    }

    const handleCancelConfirm = () => {
        setConfirmation({
            open: true,
            title: t('cancel_confirm_title'),
            description: t('cancel_confirm_description'),
            onConfirm: handleCancel,
        })
    }

    const handleReject = () => {
        setModal<RejectModalProps>('DayOffModals/RejectModal/index', {
            dayOff: dayOffData as DayOffResponse,
            refetch: () => refetch(),
        })
    }

    const handleLeaderApprove = async (feedback: string) => {
        if (!dayOffData?.day_off_id) return
        try {
            await leaderApprove({
                id: dayOffData.day_off_id,
                feedback: { day_off_feedback_lead: feedback },
            })
            toast.success(t('approve_success'))
            refetch()
            refetchAll()
        } catch (error) {
            console.error(error)
        }
    }

    const handleBodApprove = async (feedback: string) => {
        if (!dayOffData?.day_off_id) return
        try {
            await bodApprove({
                id: dayOffData.day_off_id,
                feedback: { day_off_feedback_bod: feedback },
            })
            toast.success(t('approve_success'))
            if (dayOffData.day_off_type === 'annual_leave') {
                employeeRefetch.refetchCurrent()
            }
            refetch()
            refetchAll()
        } catch (error) {
            console.error(error)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {/* Header */}
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.dayOffAccountList,
                        title: t('day_off_list'),
                    },
                    {
                        path: '',
                        title: t('leave_request_id', {
                            id: dayOffData?.day_off_id,
                        }),
                    },
                ]}
            />

            {/* Status Alert */}
            {dayOffData?.day_off_status === 'rejected' && (
                <Paper className="p-3 bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <Typogaphy className="font-medium text-red-700">
                            {t('alert_rejected')}
                        </Typogaphy>
                    </div>
                </Paper>
            )}

            {dayOffData?.day_off_status === 'cancelled' && (
                <Paper className="p-3 bg-gray-100 border border-gray-200">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-gray-500" />
                        <Typogaphy className="font-medium text-gray-700">
                            {t('alert_cancelled')}
                        </Typogaphy>
                    </div>
                </Paper>
            )}

            {/* Main Information */}
            <GridInformation
                data={gridData}
                head={{
                    title: t('day_off_information'),
                }}
            />

            {/* Day Off Dates */}
            <Paper className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <Typogaphy className="font-semibold">
                        {t('day_off_dates')} (
                        {dayOffData?.day_off_range_time?.length || 0})
                    </Typogaphy>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {dayOffData?.day_off_range_time?.map((item, index) => (
                        <DayOffDateCard key={index} item={item} t={t} />
                    ))}
                </div>
            </Paper>

            {/* Approval Status */}
            <Paper className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <Typogaphy className="font-semibold">
                        {t('approval_status')}
                    </Typogaphy>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <ApprovalStatusCard
                        title={t('leader')}
                        isApproved={isLeaderApproved || false}
                        approverName={dayOffData?.day_off_leader_name}
                        approvedAt={dayOffData?.day_off_leader_confirmed_at}
                        feedback={dayOffData?.day_off_feedback_lead}
                        icon={<User className="w-4 h-4 text-blue-500" />}
                        t={t}
                        canApprove={canLeaderApprove}
                        isApproving={isLeaderApproving}
                        onApprove={handleLeaderApprove}
                        isDisabled={isCancelledOrRejected || isLeaderSkipped}
                    />
                    <ApprovalStatusCard
                        title={t('bom_bod')}
                        isApproved={dayOffData?.day_off_bod_confirmed || false}
                        approverName={dayOffData?.day_off_bod_name}
                        approvedAt={dayOffData?.day_off_bod_confirmed_at}
                        feedback={dayOffData?.day_off_feedback_bod}
                        icon={<Briefcase className="w-4 h-4 text-purple-500" />}
                        t={t}
                        canApprove={canBodApprove}
                        isApproving={isBodApproving}
                        onApprove={handleBodApprove}
                        waitingForDependency={
                            !isAdmin && !isLeaderApproved
                                ? t('waiting_for_leader')
                                : undefined
                        }
                        isDisabled={isCancelledOrRejected}
                    />
                </div>
            </Paper>

            {/* Action Buttons */}
            <ButtonGroupActions
                actions={[
                    {
                        icon: <CloseIcons />,
                        label: t('cancel_day_off'),
                        onClick: handleCancelConfirm,
                        variant: 'outlined',
                        disabled: !canCancel || isCancelingDO,
                    },
                    {
                        icon: <XCircle className="w-4 h-4" />,
                        label: t('reject_day_off'),
                        onClick: handleReject,
                        variant: 'contained',
                        color: 'error',
                        disabled: !canReject,
                    },
                ]}
            />
        </div>
    )
}

export const DetailDayOff = React.memo(() => {
    return <Detail />
})

export default Detail
